"use no memo";

/* eslint-disable complexity */
import { isEmpty, pick } from 'lodash-es';

import { isYelpPlatform } from '@local/utils';

import { onlyNums } from 'helpers/format';
import { FULFILLMENT_METHODS } from 'helpers/order';
import {
  APPLE_PAY_STRUCT,
  GOOGLE_PAY_STRUCT,
  serializeCard,
} from 'helpers/payments';

import { UserDataType } from 'types/customer';

import { LocalOrderType } from '.';
import buildOrderPayload, { BuildOrderPayload } from './buildOrderPayload';

export type SubmitOrderPayload = {
  phone?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  cardData?: {
    address: { zip: string };
    id: string | null;
    token: string;
    type: string;
  };
};

function serializeItemForSubmit(
  item: ReturnType<typeof buildOrderPayload>['items'][number]
) {
  // sort mods by id so they list properly in admin and on tablet
  const sortedMods = item.modifier_categories.map((modCat) =>
    // FIXME: verify these are actually strings and not potentially numbers
    modCat.modifiers.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
  );

  return {
    ...item,
    modifier_categories: item.modifier_categories.map((modCat, i) => ({
      ...modCat,
      modifiers: sortedMods[i],
    })),
  };
}

export default function buildSubmitOrderPayload({
  payload,
  order,
  user,
  rewards,
  rewardsOptIn,
  marketingOptIn,
}: {
  payload?: SubmitOrderPayload /** payload is undefined if submitting through Yelp */;
  order: LocalOrderType & { menu_id: string };
  user: UserDataType;
  rewards: unknown[]; // $TSFIXME: type when typed in UserContext
  rewardsOptIn?: { optInStatus?: boolean };
  marketingOptIn?: { email_opt_in: boolean; sms_opt_in: boolean };
}) {
  const orderPayload = buildOrderPayload(order, user);

  const isApplePay = payload?.cardData?.type === APPLE_PAY_STRUCT.type;
  const isGooglePay = payload?.cardData?.type === GOOGLE_PAY_STRUCT.type;

  let customer: BuildOrderPayload['customer'] = pick(user, [
    'email',
    'first_name',
    'id',
    'last_name',
    'phone',
  ]);

  // In the case of guest checkout, this is the customer information needed to submit order
  if (isEmpty(customer) && (isApplePay || isGooglePay)) {
    customer = {
      email: payload.email,
      first_name: payload.first_name,
      id: null,
      last_name: payload.last_name,
    };
  }

  if (!isEmpty(customer) && !rewards?.[0]) {
    customer = {
      ...customer,
      rewards_opt_in: rewardsOptIn?.optInStatus,
    };
  }

  if (marketingOptIn) {
    customer = {
      ...customer,
      restaurant_marketing_consent: marketingOptIn,
    };
  }

  // TODO: fallback to form value if new user
  // Send the the social pay phone number only for unauthed orders
  const phoneNumber =
    (isApplePay || isGooglePay) &&
    (isEmpty(customer) || !customer.phone?.number)
      ? payload.phone
      : customer.phone?.number;

  const billingObject = payload
    ? {
        card:
          isApplePay || isGooglePay ? payload.cardData : serializeCard(payload),
      }
    : null;

  const orderSubmitPayload: BuildOrderPayload & { validated_total: number } = {
    ...orderPayload,
    items: (orderPayload.items || []).map(serializeItemForSubmit),
    customer: {
      ...orderPayload.customer,
      ...customer,
      phone: {
        number: onlyNums(phoneNumber)!,
      },
      delivery: orderPayload.customer?.delivery ?? null,
      billing: billingObject,
    },
    validated_total: order.total!,
  };

  // For pickup orders, we do not have a customer, but a customer key must exist in the payload
  if (order.fulfill_method === FULFILLMENT_METHODS.pickup && isYelpPlatform()) {
    orderSubmitPayload.customer = null;
  }

  return orderSubmitPayload;
}
